import { AlertTriangle, Shield } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { BackgroundAccessConsentModal } from "@/components/flows/BackgroundAccessConsentModal";
import { useToast } from "@/components/shared";
import { getApiErrorMessage } from "@/lib/api";
import { microAppService } from "@/lib/services";
import type { MicroAppAuthorization } from "@/types";

/**
 * Lets a dashboard's data owner authorize (and re-authorize) author-delegated
 * live data, so shared viewers get current data even when the owner is offline.
 *
 * Mirrors the scheduled-flow `ScheduledFlowAuthControl` and reuses the shared
 * `BackgroundAccessConsentModal`. Only shown to the owner of an app that has
 * author-mode bindings and needs authorization.
 */
export function MicroAppAuthControl({ appUuid }: Readonly<{ appUuid: string }>) {
  const { showToast } = useToast();
  const [auth, setAuth] = useState<MicroAppAuthorization | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showConsent, setShowConsent] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await microAppService.getAuthorization(appUuid);
      setAuth(res.data);
    } catch {
      setAuth(null);
    } finally {
      setLoading(false);
    }
  }, [appUuid]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading || !auth) return null;
  // Only the owner acts on it; and only when the app actually needs author auth
  // and it isn't already fully authorized.
  if (!auth.requiresAuthorization || !auth.isOwner) return null;
  if (auth.status === "disabled" || auth.status === "authorized") return null;

  const needsReauth = auth.status === "needs_reauth";

  const authorize = async () => {
    try {
      setBusy(true);
      const res = await microAppService.authorize(appUuid);
      setAuth(res.data);
      setShowConsent(false);
      showToast("Live data authorized", "success");
    } catch (err) {
      showToast(getApiErrorMessage(err) || "Failed to authorize live data", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-4 mt-3 px-4 py-3 bg-status-warning/10 border border-status-warning/30 rounded-lg">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-start gap-2 min-w-0">
          <Shield className="h-4 w-4 text-status-warning mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-content-primary">Authorize live data</p>
            <p className="text-xs text-content-tertiary">
              This dashboard uses your Microsoft 365 access so shared viewers see current data even when you're offline.
              Authorize it to keep the data live.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowConsent(true)}
          disabled={busy}
          className="shrink-0 inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-brand-primary hover:bg-brand-primary/90 rounded-lg disabled:opacity-50"
        >
          {needsReauth ? "Re-authorize" : "Authorize"}
        </button>
      </div>

      {needsReauth && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-status-warning">
          <AlertTriangle className="h-3.5 w-3.5" />
          Your saved permission is no longer valid. Re-authorize to keep live data working.
        </p>
      )}

      {showConsent && (
        <BackgroundAccessConsentModal
          busy={busy}
          onCancel={() => setShowConsent(false)}
          onConfirm={() => void authorize()}
          message={
            <p>
              This dashboard reads your Microsoft 365 data (e.g. Outlook mail and calendar) on your behalf so people you
              share it with see current data even when you're offline. Anthm will not gain access to anything you cannot
              already access.
            </p>
          }
        />
      )}
    </div>
  );
}
